/**
 * OverviewTab — the study's AI summary (TextArtifactPanel) plus the live
 * stipend/compensation card. Stipend edits write studies.stipend_summary
 * directly; the AI path (stipend_info artifact) publishes into the same field.
 */
import { useState } from 'react';
import { DollarSign, Loader2, Sparkles } from 'lucide-react';
import api from '../../../services/api';
import { StudyDetailResponse, StipendSummary } from '../../types';
import TextArtifactPanel, { latestArtifact } from './TextArtifactPanel';

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function StipendCard({ detail, isAdmin, onChanged }: { detail: StudyDetailResponse; isAdmin: boolean; onChanged: () => void }) {
  const { study } = detail;
  const stipendArtifact = latestArtifact(detail.artifacts, 'stipend_info');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<StipendSummary>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasExtractedDoc = detail.documents.some((d) => d.extraction_status === 'complete');

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const save = () => run('Save', async () => {
    await api.patch(`/api/crm/studies/${study.id}`, { stipend_summary: form });
    // Saving a manual value finalizes it — set aside any pending AI extraction so
    // its "AI found … Use this" banner stops re-appearing on reload. Non-fatal.
    if (stipendArtifact && ['draft', 'failed'].includes(stipendArtifact.status)) {
      try { await api.post(`/api/crm/artifacts/${stipendArtifact.id}/discard`, {}); } catch { /* non-fatal */ }
    }
    setEditing(false);
  });
  const generate = () => run('Generate', () =>
    api.post(`/api/crm/studies/${study.id}/generate`, { types: ['stipend_info'] })
  );
  const applyDraft = () => run('Apply', () => api.post(`/api/crm/artifacts/${stipendArtifact!.id}/publish`, {}));
  // Keep the manual value and set the AI find aside so the banner stops nudging.
  const dismissDraft = () => run('Dismiss', () => api.post(`/api/crm/artifacts/${stipendArtifact!.id}/discard`, {}));

  const s = study.stipend_summary || {};
  const empty = !s.amount_text && !s.schedule_text && !s.notes;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" /> Stipend &amp; compensation
        </h2>
        {isAdmin && !editing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setForm({ amount_text: s.amount_text || '', schedule_text: s.schedule_text || '', notes: s.notes || '' }); setEditing(true); }}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={busy !== null || !hasExtractedDoc || stipendArtifact?.status === 'generating'}
              title={!hasExtractedDoc ? 'Upload a protocol/ICF in Documents first' : undefined}
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
            >
              <Sparkles className="h-3.5 w-3.5" /> Extract from document
            </button>
          </div>
        )}
      </div>

      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {isAdmin && stipendArtifact?.status === 'generating' && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-primary-50 p-3 text-sm text-primary-800 dark:bg-primary-900/20 dark:text-primary-200">
          <Loader2 className="h-4 w-4 animate-spin" /> Extracting compensation details…
        </div>
      )}
      {stipendArtifact?.status === 'draft' && isAdmin && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <p className="text-xs font-medium text-amber-800 dark:text-amber-300">AI found:</p>
          <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">
            {stipendArtifact.content?.amount_text || 'No amount stated'}
            {stipendArtifact.content?.schedule_text && <> — {stipendArtifact.content.schedule_text}</>}
          </p>
          {stipendArtifact.content?.notes && <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{stipendArtifact.content.notes}</p>}
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={applyDraft}
              disabled={busy !== null}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
            >
              {busy === 'Apply' ? 'Applying…' : 'Use this'}
            </button>
            <button
              type="button"
              onClick={dismissDraft}
              disabled={busy !== null}
              title="Keep your current compensation details and dismiss this AI suggestion"
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              {busy === 'Dismiss' ? 'Dismissing…' : 'Dismiss'}
            </button>
          </div>
        </div>
      )}
      {isAdmin && stipendArtifact?.status === 'failed' && (
        <p className="mb-3 rounded-lg bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
          Extraction failed: {stipendArtifact.error}
        </p>
      )}

      {editing ? (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Amount (headline)</label>
            <input type="text" placeholder='e.g. "Up to $1,050 total"' value={form.amount_text || ''} className={inputCls}
              onChange={(e) => setForm({ ...form, amount_text: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Payment schedule</label>
            <input type="text" placeholder='e.g. "$75 per completed visit"' value={form.schedule_text || ''} className={inputCls}
              onChange={(e) => setForm({ ...form, schedule_text: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Notes (travel, payment method…)</label>
            <textarea rows={2} value={form.notes || ''} className={inputCls}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(false)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={busy !== null}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50">
              {busy === 'Save' ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : empty ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No compensation details yet.{isAdmin && ' Edit manually or extract from the uploaded document.'}
        </p>
      ) : (
        <div>
          {s.amount_text && <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{s.amount_text}</p>}
          {s.schedule_text && <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">{s.schedule_text}</p>}
          {s.notes && <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{s.notes}</p>}
        </div>
      )}
    </div>
  );
}

export default function OverviewTab({ detail, isAdmin, onChanged }: { detail: StudyDetailResponse; isAdmin: boolean; onChanged: () => void }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <TextArtifactPanel
          detail={detail}
          artifactType="summary"
          title="Study summary"
          emptyHint="No summary yet. Upload the protocol in Documents, then generate a recruiter-facing summary."
          isAdmin={isAdmin}
          onChanged={onChanged}
        />
      </div>
      <div>
        <StipendCard detail={detail} isAdmin={isAdmin} onChanged={onChanged} />
      </div>
    </div>
  );
}
